import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { ChevronDown } from 'lucide-react';

const TornadoRings = () => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const ringGroupsRef = useRef([]);
  const animationFrameRef = useRef(null);
  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeItems, setActiveItems] = useState([]);
  const [currentMobileIndex, setCurrentMobileIndex] = useState(0);
  const carouselIntervalRef = useRef(null);
  const touchStartRef = useRef(0);
  const touchEndRef = useRef(0);

  const SETTINGS = {
    numStrips: 3,
    stripWidth: 0.8,
    height: 0.8,
    rotationSpeed: 3,
    wobbleIntensity: 1,
    waveIntensity: 1,
    colors: {
      primary: 0xff6b35,
      secondary: 0xffa500,
      accent: 0x2a2a2a
    }
  };

  const RING_SETTINGS = [
    { radius: 0.8, tilt: {x: 10, y: 0, z: 0},  offset: {x: 0, y: 0, z: 0} },
    { radius: 1.2, tilt: {x: -10, y: 0, z: 0}, offset: {x: 0, y: 1, z: 0} },
    { radius: 1.6, tilt: {x: 10, y: 0, z: 0},  offset: {x: 0, y: 2, z: 0} },
    { radius: 2.0, tilt: {x: 0, y: 0, z: 0}, offset: {x: 0, y: 3, z: 0} },
    { radius: 2.4, tilt: {x: -10, y: 0, z: -10},  offset: {x: 0, y: 4.3, z: 0} },
    { radius: 2.8, tilt: {x: -10, y: 0, z: 2}, offset: {x: 0, y: 5.5, z: 0} },
    { radius: 3.2, tilt: {x: -10, y: 0, z: -10},  offset: {x: 0, y: 7, z: 0} }
  ];

  const RING_DESCRIPTIONS = [
    { title: "2019", desc: "Signed an agreement for PNG stove technology manufacturing, marking the beginning of our journey." },
    { title: "2021", desc: "Partnered with PCRA for a nationwide radio campaign that boosted stove sales and validated demand across India." },
    { title: "2021", desc: "Encouraged by strong customer response, we applied for BIS certification and initiated structured marketing efforts." },
    { title: "2022", desc: "Collaborated with CGD companies to explore challenges and opportunities in scaling PNG stove adoption." },
    { title: "2023", desc: "Honoured with an award from CSIR-IIP for innovation in PNG stove technology." },
    { title: "2024", desc: "Signed three MoUs with CGD companies and presented PNG stove insights to PNGRB in Delhi at the national level." },
    { title: "2024-2025", desc: "In 2024–25, RealFlame piloted PNG stoves and mapped a roadmap for safe, large-scale implementation." }
  ];

  const createStrip = (data) => {
    const points = [];
    const segments = 60;
    for (let i = 0; i <= segments; i++) {
      const y = (i / segments) * SETTINGS.height - SETTINGS.height / 2;
      points.push(new THREE.Vector3(0, y, 0));
    }

    const stripWidth = SETTINGS.stripWidth;
    const geometry = new THREE.BufferGeometry();
    const vertices = [];
    const indices = [];
    const uvs = [];

    for (let i = 0; i < points.length; i++) {
      const t = i / (points.length - 1);
      const point = points[i];
      const right = new THREE.Vector3(stripWidth, 0, 0);
      const left = new THREE.Vector3(-stripWidth, 0, 0);

      vertices.push(
        point.x + left.x, point.y + left.y, point.z + left.z,
        point.x + right.x, point.y + right.y, point.z + right.z
      );

      uvs.push(0, t, 1, t);

      if (i < points.length - 1) {
        const base = i * 2;
        indices.push(base, base + 1, base + 2);
        indices.push(base + 1, base + 3, base + 2);
      }
    }

    geometry.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    let color;
    if (data.colorType === 0) color = new THREE.Color(SETTINGS.colors.primary);
    else if (data.colorType === 1) color = new THREE.Color(SETTINGS.colors.secondary);
    else color = new THREE.Color(SETTINGS.colors.accent);

    const material = new THREE.MeshStandardMaterial({
      color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.9,
      metalness: 0.0,
      roughness: 0.2,
      emissive: color,
      emissiveIntensity: 0.8
    });

    return new THREE.Mesh(geometry, material);
  };

  const updateStrip = (strip, data, time) => {
    const geometry = strip.geometry;
    const positions = geometry.attributes.position.array;
    const numPoints = positions.length / 6;

    const visiblePoints = Math.floor(numPoints * data.formProgress);

    for (let i = 0; i < numPoints; i++) {
      const t = i / (numPoints - 1);
      const y = t * SETTINGS.height - SETTINGS.height / 2;

      const baseRadius = data.radius * data.formProgress;
      const wave = Math.sin(time * 2 + t * 5 + data.wobblePhase) * SETTINGS.waveIntensity * 0.3;
      const wobble = Math.sin(time * 2.5 + t * 10 + data.wobblePhase) * SETTINGS.wobbleIntensity * 0.2;

      const rotation = time * data.speed + data.angleOffset;
      const totalAngle = rotation + t * Math.PI * 4;
      const radius = baseRadius + wave + wobble;

      let x, z;
      if (i > visiblePoints || data.formProgress === 0) {
        x = 0;
        z = 0;
      } else {
        x = Math.cos(totalAngle) * radius;
        z = Math.sin(totalAngle) * radius;
      }

      const stripWidth = SETTINGS.stripWidth * (1 + t * 0.2);
      const perpX = -Math.sin(totalAngle);
      const perpZ = Math.cos(totalAngle);

      positions[i * 6] = x - perpX * stripWidth;
      positions[i * 6 + 1] = y;
      positions[i * 6 + 2] = z - perpZ * stripWidth;

      positions[i * 6 + 3] = x + perpX * stripWidth;
      positions[i * 6 + 4] = y;
      positions[i * 6 + 5] = z + perpZ * stripWidth;
    }

    geometry.attributes.position.needsUpdate = true;
    geometry.computeVertexNormals();
  };

  useEffect(() => {
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);
    sceneRef.current = scene;

    const canvas = canvasRef.current;
    const container = containerRef.current;
    
    let camera, renderer;
    const width = container.clientWidth;
    const currentIsMobile = window.innerWidth <= 768;
    
    if (currentIsMobile) {
      const height = 700;
      camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
      camera.position.set(0, 3.5, 12);
      camera.lookAt(0, 3.5, 0);
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
      renderer.setSize(width, height);
    } else {
      const height = window.innerHeight;
      camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
      camera.position.set(8, 5, 10);
      camera.lookAt(0, 4, 0);
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
      renderer.setSize(width, height);
    }
    
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    cameraRef.current = camera;
    rendererRef.current = renderer;

    const ringGroups = [];
    RING_SETTINGS.forEach((ringCfg, ringIndex) => {
      const group = new THREE.Group();

      const tiltRad = {
        x: THREE.MathUtils.degToRad(ringCfg.tilt.x),
        y: THREE.MathUtils.degToRad(ringCfg.tilt.y),
        z: THREE.MathUtils.degToRad(ringCfg.tilt.z)
      };
      group.rotation.set(tiltRad.x, tiltRad.y, tiltRad.z);
      group.position.set(ringCfg.offset.x, ringCfg.offset.y, ringCfg.offset.z);

      const ringRibbons = [];

      for (let i = 0; i < SETTINGS.numStrips; i++) {
        const stripData = {
          angleOffset: (i / SETTINGS.numStrips) * Math.PI * 2,
          speed: SETTINGS.rotationSpeed * (0.8 + Math.random() * 0.4),
          wobblePhase: Math.random() * Math.PI * 2,
          colorType: Math.floor(Math.random() * 3),
          radius: ringCfg.radius,
          formProgress: 1,
          visible: true
        };

        const strip = createStrip(stripData);
        group.add(strip);
        strip.material.opacity = 0.9;
        ringRibbons.push({ mesh: strip, data: stripData });
      }

      scene.add(group);
      ringGroups.push({ group, ribbons: ringRibbons, index: ringIndex });
    });

    ringGroupsRef.current = ringGroups;

    const ambientLight = new THREE.AmbientLight(0xffffff, 1.5);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(5, 10, 5);
    scene.add(dirLight);

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);
      const time = Date.now() * 0.001;
      
      ringGroups.forEach(ringGroup => {
        ringGroup.ribbons.forEach(({ mesh, data }) => {
          updateStrip(mesh, data, time);
        });
      });
      
      renderer.render(scene, camera);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      renderer.dispose();
      scene.traverse((object) => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
    };
  }, []);

  useEffect(() => {
    if (isMobile) return;
    
    const handleScroll = () => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const containerHeight = container.clientHeight;
      
      const scrollStart = rect.top;
      const scrollRange = containerHeight - viewportHeight;
      const scrollProgress = Math.max(0, Math.min(1, -scrollStart / scrollRange));

      const totalRings = ringGroupsRef.current.length;
      const newActiveItems = [];

      ringGroupsRef.current.forEach((ringGroup, index) => {
        const ringProgress = (index + 1) / totalRings;
        const isActive = scrollProgress >= ringProgress * 0.7;

        if (isActive) {
          newActiveItems.push(index);
        }
      });

      setActiveItems(newActiveItems);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile]);

  useEffect(() => {
    const handleResize = () => {
      const container = containerRef.current;
      const camera = cameraRef.current;
      const renderer = rendererRef.current;
      
      if (!container || !camera || !renderer) return;
      
      const wasMobile = isMobile;
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
      
      if (wasMobile !== newIsMobile) {
        setActiveItems([]);
      }
      
      if (newIsMobile) {
        const width = container.clientWidth;
        const height = 700;
        camera.aspect = width / height;
        camera.fov = 60;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
        camera.position.set(0, 3.5, 16);
        camera.lookAt(0, 3.5, 0);
      } else {
        const width = container.clientWidth;
        const height = window.innerHeight;
        camera.aspect = width / height;
        camera.fov = 60;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
        camera.position.set(8, 5, 10);
        camera.lookAt(0, 4, 0);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;

    const startAutoplay = () => {
      carouselIntervalRef.current = setInterval(() => {
        setCurrentMobileIndex(prev => (prev + 1) % RING_DESCRIPTIONS.length);
      }, 3500);
    };

    startAutoplay();

    return () => {
      if (carouselIntervalRef.current) {
        clearInterval(carouselIntervalRef.current);
      }
    };
  }, [isMobile]);

  const handleTouchStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (carouselIntervalRef.current) {
      clearInterval(carouselIntervalRef.current);
    }

    const swipeDistance = touchStartRef.current - touchEndRef.current;
    const minSwipeDistance = 50;

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        setCurrentMobileIndex(prev => (prev + 1) % RING_DESCRIPTIONS.length);
      } else {
        setCurrentMobileIndex(prev => (prev - 1 + RING_DESCRIPTIONS.length) % RING_DESCRIPTIONS.length);
      }
    }

    carouselIntervalRef.current = setInterval(() => {
      setCurrentMobileIndex(prev => (prev + 1) % RING_DESCRIPTIONS.length);
    }, 3500);
  };

  return (
    <div ref={containerRef} style={{ 
      position: 'relative',
      width: '100%',
      minHeight: isMobile ? 'auto' : '500vh',
      backgroundColor: '#ffffff'
    }}>
      <style>{`
        .tornado-canvas-wrapper {
          position: ${isMobile ? 'relative' : 'sticky'};
          top: ${isMobile ? '0' : '0'};
          width: 100%;
          height: ${isMobile ? 'auto' : '100vh'};
          overflow: hidden;
          z-index: 1;
        }
        
        .tornado-canvas-wrapper canvas {
          display: block;
          width: 100%;
          height: ${isMobile ? '700px' : '100%'};
        }
        
        .tornado-scroll-arrow {
          position: absolute;
          right: 40px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 100;
          pointer-events: none;
          animation: bounceArrow 2s ease-in-out infinite;
          display: ${isMobile ? 'none' : 'flex'};
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }
        
        .tornado-scroll-arrow-text {
          font-size: 14px;
          font-weight: 600;
          color: #ff6b35;
          text-transform: uppercase;
          letter-spacing: 1px;
          writing-mode: vertical-rl;
          text-orientation: mixed;
        }
        
        .tornado-scroll-arrow-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #ff6b35, #ffa500);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(255, 107, 53, 0.3);
        }
        
        @keyframes bounceArrow {
          0%, 100% {
            transform: translateY(-50%);
          }
          50% {
            transform: translateY(calc(-50% + 10px));
          }
        }
        
        .tornado-timeline-container {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          display: ${isMobile ? 'none' : 'flex'};
          align-items: center;
          justify-content: space-between;
          padding: 0 40px;
        }
        
        .tornado-mobile-content {
          display: ${isMobile ? 'block' : 'none'};
          position: absolute;
          bottom: 20px;
          right: 20px;
          width: calc(100% - 40px);
          max-width: 320px;
          pointer-events: auto;
          z-index: 10;
        }
        
        .tornado-carousel-container {
          position: relative;
          width: 100%;
          overflow: hidden;
          touch-action: pan-x;
        }
        
        .tornado-carousel-wrapper {
          display: flex;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .tornado-carousel-item {
          min-width: 100%;
          padding: 0;
        }
        
        .tornado-card {
          background: #ffffff;
           border: 2px solid transparent;
  border-image: linear-gradient(
    90deg, 
    #ff6b35 0%, 
    #ffc906 80%, 
    #4d4d4d 100%
  );
  border-image-slice: 1;
  border-radius: 16px;
          padding: 20px;
        }
        
        .tornado-card h3 {
          font-size: 18px;
          font-weight: 700;
          color: #ff6b35;
          margin: 0 0 10px 0;
          text-align: left;
        }
        
        .tornado-card p {
          font-size: 13px;
          line-height: 1.5;
          color: #333;
          margin: 0;
          text-align: left;
        }
        
        .tornado-carousel-indicators {
          display: flex;
          justify-content: center;
          gap: 6px;
          margin-top: 12px;
        }
        
        .tornado-carousel-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255, 107, 53, 0.3);
          transition: all 0.3s ease;
          cursor: pointer;
        }
        
        .tornado-carousel-dot.active {
          background: #ff6b35;
          width: 24px;
          border-radius: 4px;
        }
        
        .tornado-timeline-item {
          opacity: 0;
          position: absolute;
          max-width: 280px;
          transition: opacity 0.5s ease, transform 0.5s ease;
          pointer-events: auto;
          padding: 20px 24px;
          transform: translateY(50px);
          background: transparent;
          border-radius: 12px;
        }
        
        .tornado-timeline-item.active {
          opacity: 1;
          transform: translateY(0);
        }
        
        .tornado-timeline-item h2 {
          font-size: 16px;
          color: #000000;
          margin: 0 0 10px 0;
          font-weight: 600;
          line-height: 1.3;
          letter-spacing: 0.2px;
        }
        
        .tornado-timeline-item p {
          font-size: 13px;
          color: #333;
          line-height: 1.6;
          margin: 0 0 12px 0;
        }
        
        .tornado-timeline-item::after {
          content: '';
          display: block;
          width: 100%;
          height: 3px;
          background: linear-gradient(90deg, #ff6b35, #ffa500);
          margin-top: 12px;
          border-radius: 2px;
        }
        
        .item-0 { bottom: 12%; left: 24%; }
        .item-2 { bottom: 31%; left: 20%; }
        .item-4 { bottom: 50%; left: 18%; }
        .item-6 { bottom: 69%; left: 16%; }
        .item-1 { bottom: 21.5%; right: 23%; text-align: right; }
        .item-3 { bottom: 40.5%; right: 19%; text-align: right; }
        .item-5 { bottom: 59.5%; right: 15%; text-align: right; }
        
        @media (max-width: 1440px) {
          .tornado-timeline-container {
            padding: 0 30px;
          }
          
          .tornado-timeline-item {
            max-width: 240px;
            padding: 18px 22px;
          }
          
          .tornado-timeline-item h2 {
            font-size: 15px;
          }
          
          .tornado-timeline-item p {
            font-size: 12px;
          }
        }
        
        @media (max-width: 1024px) {
          .tornado-timeline-container {
            padding: 0 20px;
          }
          
          .tornado-timeline-item {
            max-width: 220px;
            padding: 16px 20px;
          }
          
          .tornado-timeline-item h2 {
            font-size: 14px;
          }
          
          .tornado-timeline-item p {
            font-size: 11px;
          }
          
          .item-0 { bottom: 12%; left: 4%; }
          .item-2 { bottom: 31%; left: 4%; }
          .item-4 { bottom: 50%; left: 4%; }
          .item-6 { bottom: 69%; left: 4%; }
          .item-1 { bottom: 21.5%; right: 4%; }
          .item-3 { bottom: 40.5%; right: 4%; }
          .item-5 { bottom: 59.5%; right: 4%; }
        }
        
        @media (max-width: 768px) {
          .tornado-mobile-content {
            bottom: 16px;
            right: 16px;
            width: calc(100% - 32px);
          }
        }
        
        @media (max-width: 480px) {
          .tornado-mobile-content {
            max-width: 280px;
          }
          
          .tornado-card {
            padding: 16px;
          }
          
          .tornado-card h3 {
            font-size: 16px;
          }
          
          .tornado-card p {
            font-size: 12px;
          }
        }
      `}</style>

      <div className="tornado-canvas-wrapper">
        <canvas ref={canvasRef} />
        
        {!isMobile && (
          <div className="tornado-scroll-arrow">
            <span className="tornado-scroll-arrow-text">Scroll To Know our PNG milestones</span>
            <div className="tornado-scroll-arrow-icon">
              <ChevronDown size={28} color="#ffffff" strokeWidth={3} />
            </div>
          </div>
        )}
        
        <div className="tornado-timeline-container">
          {RING_DESCRIPTIONS.map((ring, index) => (
            <div 
              key={index}
              className={`tornado-timeline-item item-${index} ${
                activeItems.includes(index) ? 'active' : ''
              }`}
            >
              <h2>{ring.title}</h2>
              <p>{ring.desc}</p>
            </div>
          ))}
        </div>

        {isMobile && (
          <div className="tornado-mobile-content">
            <div 
              className="tornado-carousel-container"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div 
                className="tornado-carousel-wrapper"
                style={{
                  transform: `translateX(-${currentMobileIndex * 100}%)`
                }}
              >
                {RING_DESCRIPTIONS.map((ring, index) => (
                  <div key={index} className="tornado-carousel-item">
                    <div className="tornado-card">
                      <h3>{ring.title}</h3>
                      <p>{ring.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="tornado-carousel-indicators">
              {RING_DESCRIPTIONS.map((_, index) => (
                <div
                  key={index}
                  className={`tornado-carousel-dot ${currentMobileIndex === index ? 'active' : ''}`}
                  onClick={() => {
                    if (carouselIntervalRef.current) {
                      clearInterval(carouselIntervalRef.current);
                    }
                    setCurrentMobileIndex(index);
                    carouselIntervalRef.current = setInterval(() => {
                      setCurrentMobileIndex(prev => (prev + 1) % RING_DESCRIPTIONS.length);
                    }, 3500);
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TornadoRings;